/**
 * CMS Label Values Repository
 *
 * 라벨 값 관리를 위한 Repository
 */

import { findOne, findMany, create, updateOne, deleteMany } from '@spfn/core/db';
import { eq, and, SQL, isNull } from 'drizzle-orm';
import { cmsLabelValues, type CmsLabelValue, type NewCmsLabelValue } from '@/server/entities';

/**
 * 특정 라벨의 특정 버전 값들 조회
 */
export async function findByLabelIdAndVersion(
    labelId: number,
    version: number,
    options?: {
        locale?: string;
        breakpoint?: string | null;
    }
): Promise<CmsLabelValue[]>
{
    const { locale, breakpoint } = options || {};

    const conditions: SQL[] = [
        eq(cmsLabelValues.labelId, labelId),
        eq(cmsLabelValues.version, version)
    ];

    if (locale)
    {
        conditions.push(eq(cmsLabelValues.locale, locale));
    }

    if (breakpoint !== undefined)
    {
        conditions.push(
            breakpoint === null
                ? isNull(cmsLabelValues.breakpoint)
                : eq(cmsLabelValues.breakpoint, breakpoint)
        );
    }

    return findMany(cmsLabelValues, {
        where: and(...conditions)
    });
}

/**
 * 값 저장 (upsert)
 * - version: null → Draft 저장 (덮어쓰기)
 * - version: number → Published 버전 생성 (불변)
 */
export async function upsert(data: NewCmsLabelValue): Promise<CmsLabelValue>
{
    // 기존 값이 있는지 확인
    const versionCondition = data.version === null || data.version === undefined
        ? isNull(cmsLabelValues.version)
        : eq(cmsLabelValues.version, data.version as number);

    const existing = await findOne(
        cmsLabelValues,
        and(
            eq(cmsLabelValues.labelId, data.labelId),
            versionCondition,
            eq(cmsLabelValues.locale, data.locale || 'ko'),
            data.breakpoint
                ? eq(cmsLabelValues.breakpoint, data.breakpoint)
                : isNull(cmsLabelValues.breakpoint)
        )
    );

    if (existing)
    {
        // UPDATE (only for drafts with version: null)
        if (data.version === null || data.version === undefined)
        {
            const updated = await updateOne(
                cmsLabelValues,
                { id: existing.id },
                { value: data.value }
            );
            return updated!;
        }
        else
        {
            // Published versions are immutable - this shouldn't happen
            throw new Error(`Published version ${data.version} already exists and cannot be overwritten`);
        }
    }
    else
    {
        // INSERT (both draft and new published versions)
        return create(cmsLabelValues, data);
    }
}

/**
 * Draft 값들 조회 (version = null)
 */
export async function findDraftsByLabelId(labelId: number): Promise<CmsLabelValue[]>
{
    return findMany(cmsLabelValues, {
        where: and(
            eq(cmsLabelValues.labelId, labelId),
            isNull(cmsLabelValues.version)
        )
    });
}

/**
 * 여러 값 일괄 저장
 */
export async function upsertMany(values: NewCmsLabelValue[]): Promise<CmsLabelValue[]>
{
    const results = [];
    for (const value of values)
    {
        const result = await upsert(value);
        results.push(result);
    }
    return results;
}

/**
 * 특정 버전의 모든 값 삭제
 */
export async function deleteByVersion(labelId: number, version: number): Promise<CmsLabelValue[]>
{
    return deleteMany(
        cmsLabelValues,
        and(
            eq(cmsLabelValues.labelId, labelId),
            eq(cmsLabelValues.version, version)
        )
    );
}

// Legacy export for backward compatibility
export const cmsLabelValuesRepository = {
    findByLabelIdAndVersion,
    findDraftsByLabelId,
    upsert,
    upsertMany,
    deleteByVersion
};